import React from "react";
import LoadingSpinner from "../components/Loading/LoadingSpinner";
import AlertBox from "../components/Popups/AlertBox";
import { FcGoogle } from "react-icons/fc";
import { HiOutlineChatBubbleBottomCenterText, HiOutlineDocumentDuplicate, HiOutlineLink } from "react-icons/hi2";
import logo from "../assets/logo.png";
import useAuthFacade from "../facades/useAuthFacade";

function SignInUser() {
  const { loading, showAlert, handleGoogleLogin, handleOnClose } = useAuthFacade();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      {loading && <LoadingSpinner />}

      <div className="max-w-4xl w-full bg-white rounded-[3rem] shadow-2xl shadow-slate-200 overflow-hidden flex flex-col md:flex-row border border-slate-100">

        {/* Left Side: Reality-based Value Proposition */}
        <div className="md:w-1/2 bg-slate-900 p-12 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-12">
              <img src={logo} alt="Logo" className="w-10 h-12 object-contain" />
              <span className="text-xl font-black tracking-tighter uppercase">
                Campus<span className="text-blue-500">Connect</span>
              </span>
            </div>

            <h1 className="text-4xl font-black leading-tight mb-6">
              Learn from <br />
              <span className="text-blue-400">Real Experiences.</span>
            </h1>

            <div className="space-y-8">
              <FeatureItem
                icon={<HiOutlineChatBubbleBottomCenterText className="text-blue-400" />}
                title="Interview Insights"
                desc="Read step-by-step interview journeys shared by seniors and peers."
              />
              <FeatureItem
                icon={<HiOutlineDocumentDuplicate className="text-blue-400" />}
                title="Shared Study Material"
                desc="Access branch-specific notes and question papers uploaded by the community."
              />
              <FeatureItem
                icon={<HiOutlineLink className="text-blue-400" />}
                title="Curated Opportunities"
                desc="Quick access to verified application links from top companies."
              />
            </div>
          </div>

          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-12 relative z-10">
            For Students • By Students
          </p>
        </div>

        {/* Right Side: Simple Google Login */}
        <div className="md:w-1/2 p-12 flex flex-col justify-center items-center text-center bg-white">
          <div className="max-w-sm w-full">
            <h2 className="text-3xl font-black text-slate-900 mb-2">Welcome</h2>
            <p className="text-slate-500 font-medium mb-10 text-sm">
              Sign in with your college or personal Google account to get started.
            </p>

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-4 bg-white border-2 border-slate-100 py-4 px-6 rounded-2xl hover:border-blue-500 hover:shadow-xl hover:shadow-blue-50 transition-all active:scale-95 group"
            >
              <FcGoogle size={28} />
              <span className="text-slate-900 font-black text-sm tracking-tight group-hover:text-blue-700">
                Continue with Google
              </span>
            </button>

            <div className="mt-12 space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[11px] text-slate-500 font-bold leading-relaxed">
                  Join <span className="text-slate-900">500+ students</span> sharing notes and interview experiences this week.
                </p>
              </div>

              <button
                onClick={() => window.location.href = "/"}
                className="text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-blue-600 transition-colors"
              >
                ← Explore as Guest
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAlert && (
        <AlertBox
          message="Authentication failed. Please try again."
          status={false}
          onClose={handleOnClose}
        />
      )}
    </div>
  );
}

const FeatureItem = ({ icon, title, desc }) => (
  <div className="flex gap-4">
    <div className="mt-1 text-2xl">{icon}</div>
    <div>
      <h3 className="font-black text-sm text-slate-100 uppercase tracking-tight">{title}</h3>
      <p className="text-slate-400 text-xs leading-relaxed font-medium mt-1">{desc}</p>
    </div>
  </div>
);

export default SignInUser;